import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { LOGO_URL, LOGO_ICON, CONTACT } from "@/config";
import { WhatsAppIcon } from "@/components/SocialIcons";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t.meta.galleryTitle;
  }, [lang, t.meta.galleryTitle]);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => {
      setItems(data.map((d) => ({ id: d.id, src: `${process.env.REACT_APP_BACKEND_URL}${d.url}` })));
    }).catch(() => setItems([]));
  }, []);

  const GALLERY = items;
  const prev = useCallback((e) => { e?.stopPropagation?.(); setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length); }, [GALLERY.length]);
  const next = useCallback((e) => { e?.stopPropagation?.(); setActive((i) => (i + 1) % GALLERY.length); }, [GALLERY.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, prev, next]);

  return (
    <div className="bg-white min-h-screen" data-testid="gallery-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/"><img src={LOGO_URL} alt="Sedlo Services logo" className="h-12 w-auto" /></Link>
          <div className="flex items-center gap-3">
            <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex btn-lift items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2.5 rounded-md">
              <WhatsAppIcon className="w-4 h-4" /> WhatsApp
            </a>
            <Link to="/" data-testid="gallery-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-brand">
              <ArrowLeft className="w-4 h-4" /> {t.galleryPage.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{t.galleryPage.eyebrow}</p>
        <h1 className="font-head text-4xl font-bold text-navy tracking-tight">{t.galleryPage.title}</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">{t.galleryPage.subtitle}</p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {GALLERY.map((img, i) => (
            <motion.button
              key={img.id}
              data-testid={`gallery-page-thumb-${i}`}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: (i % 4) * 0.04 }}
              className="group relative aspect-square overflow-hidden rounded-md bg-slate-100"
            >
              <img src={img.src} alt="Sedlo Services" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <img src={LOGO_URL} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 w-14 h-auto opacity-25 drop-shadow" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.button>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="gallery-page-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-navy-deep/95 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <button data-testid="gallery-page-lightbox-close" aria-label="Zavrieť" onClick={() => setActive(null)} className="absolute top-5 right-5 text-white/80 hover:text-white p-2"><X className="w-8 h-8" /></button>
            <button data-testid="gallery-page-lightbox-prev" aria-label="Predchádzajúca fotka" onClick={prev} className="absolute left-3 sm:left-8 text-white/70 hover:text-white p-2"><ChevronLeft className="w-9 h-9" /></button>
            <button data-testid="gallery-page-lightbox-next" aria-label="Ďalšia fotka" onClick={next} className="absolute right-3 sm:right-8 text-white/70 hover:text-white p-2"><ChevronRight className="w-9 h-9" /></button>
            <motion.div key={active} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative max-w-5xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              <img src={GALLERY[active].src} alt="Sedlo Services" className="max-h-[85vh] w-auto rounded-md object-contain" />
              <img src={LOGO_ICON} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 w-16 h-auto opacity-25 drop-shadow-lg" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
